import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';

class NotificationService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.pushToken = null;
    this.listeners = [];
  }

  /**
   * Initialize push notifications
   * Call this on app startup
   */
  async initialize() {
    if (!this.isNative) {
      console.log('Push notifications are only available on native platforms');
      return { success: false, message: 'Not a native platform' };
    }

    try {
      // Check and request permissions
      const permResult = await this.requestPermissions();
      if (!permResult.granted) {
        return { success: false, message: 'Permissions not granted' };
      }

      // Register for push notifications
      await PushNotifications.register();

      // Set up listeners
      this.setupPushListeners();
      this.setupLocalNotificationListeners();

      return { success: true, message: 'Notifications initialized' };
    } catch (error) {
      console.error('Error initializing notifications:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * Request notification permissions
   */
  async requestPermissions() {
    if (!this.isNative) {
      return { granted: false };
    }

    try {
      // Check current permission status
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        // Request permissions
        permStatus = await PushNotifications.requestPermissions();
      }

      // Also request local notification permissions
      const localPermStatus = await LocalNotifications.requestPermissions();

      return {
        granted: permStatus.receive === 'granted',
        pushPermission: permStatus.receive,
        localPermission: localPermStatus.display,
      };
    } catch (error) {
      console.error('Error requesting permissions:', error);
      return { granted: false, error: error.message };
    }
  }

  /**
   * Set up push notification listeners
   */
  setupPushListeners() {
    // Registration success
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration success, token:', token.value);
      this.pushToken = token.value;
      this.notifyListeners('registration', { token: token.value });
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
      this.notifyListeners('registrationError', { error });
    });

    // Notification received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      this.notifyListeners('pushReceived', { notification });

      // Show local notification when app is in foreground
      this.showLocalNotification({
        title: notification.title || 'New Notification',
        body: notification.body || '',
        data: notification.data,
      });
    });

    // Notification tapped
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      this.notifyListeners('pushTapped', { action });
    });
  }

  /**
   * Set up local notification listeners
   */
  setupLocalNotificationListeners() {
    // Local notification received
    LocalNotifications.addListener('localNotificationReceived', (notification) => {
      console.log('Local notification received:', notification);
      this.notifyListeners('localReceived', { notification });
    });

    // Local notification tapped
    LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      console.log('Local notification action performed:', action);
      this.notifyListeners('localTapped', { action });
    });
  }

  /**
   * Show a local notification
   * @param {Object} options - Notification options
   * @param {string} options.title - Notification title
   * @param {string} options.body - Notification body
   * @param {Object} options.data - Extra data to attach
   * @param {number} options.id - Notification ID (auto-generated if not provided)
   * @param {Date} options.schedule - Schedule for later (optional)
   */
  async showLocalNotification(options) {
    if (!this.isNative) {
      console.log('Local notification (web fallback):', options);
      // Web fallback using browser notifications
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(options.title, { body: options.body });
      }
      return;
    }

    try {
      const notificationOptions = {
        notifications: [
          {
            id: options.id || Math.floor(Math.random() * 100000),
            title: options.title,
            body: options.body,
            extra: options.data || {},
            sound: 'old_phone_ring_tone.mp3',
            channelId: 'order_alerts_v2',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
          },
        ],
      };

      // Schedule for later if requested
      if (options.schedule) {
        notificationOptions.notifications[0].schedule = {
          at: options.schedule,
        };
      }

      await LocalNotifications.schedule(notificationOptions);
    } catch (error) {
      console.error('Error showing local notification:', error);
    }
  }

  /**
   * Cancel a scheduled local notification
   * @param {number} id - Notification ID to cancel
   */
  async cancelLocalNotification(id) {
    if (!this.isNative) return;

    try {
      await LocalNotifications.cancel({ notifications: [{ id }] });
    } catch (error) {
      console.error('Error canceling notification:', error);
    }
  }

  /**
   * Cancel all pending notifications
   */
  async cancelAllNotifications() {
    if (!this.isNative) return;

    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel(pending);
      }
    } catch (error) {
      console.error('Error canceling all notifications:', error);
    }
  }

  /**
   * Get push notification token
   * @returns {string|null} The push token
   */
  getPushToken() {
    return this.pushToken;
  }

  /**
   * Add a listener for notification events
   * @param {string} event - Event type
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  addListener(event, callback) {
    const listener = { event, callback };
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of an event
   * @param {string} event - Event type
   * @param {Object} data - Event data
   */
  notifyListeners(event, data) {
    this.listeners
      .filter((l) => l.event === event)
      .forEach((l) => l.callback(data));
  }

  /**
   * Remove all listeners
   */
  async removeAllListeners() {
    this.listeners = [];
    if (this.isNative) {
      await PushNotifications.removeAllListeners();
      await LocalNotifications.removeAllListeners();
    }
  }

  /**
   * Get delivered notifications (iOS only)
   */
  async getDeliveredNotifications() {
    if (!this.isNative) return { notifications: [] };

    try {
      return await PushNotifications.getDeliveredNotifications();
    } catch (error) {
      console.error('Error getting delivered notifications:', error);
      return { notifications: [] };
    }
  }

  /**
   * Remove delivered notifications (iOS only)
   */
  async removeDeliveredNotifications() {
    if (!this.isNative) return;

    try {
      const delivered = await this.getDeliveredNotifications();
      if (delivered.notifications.length > 0) {
        await PushNotifications.removeDeliveredNotifications(delivered);
      }
    } catch (error) {
      console.error('Error removing delivered notifications:', error);
    }
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
